import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Link as LinkMui,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { get, isEmpty, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  BottomSafeArea,
  LoadingButton,
  LoadingSpinner,
  RadioGroupWithOption,
  WixNavigationBar,
  apiAxios,
  useHttpRequest,
} from '../../components';
import { APP_NAME } from '../../config';
import { createScopedI18n, i18n } from '../../i18n/i18n';
import { joinPairs, labelFieldEnum } from '../../utils/libs';
import { SelectWithOption } from '../../components/select_with_option';

const registerPageI18n = createScopedI18n('pages.register');
const registerFieldI18n = createScopedI18n('graphql.fields.register');
const registerErrorI18n = createScopedI18n('graphql.errors.types.register.fields', { joinOutput: true });

const registerUserSchema = Yup.object({
  name: Yup.string().required(registerErrorI18n('name.blank')),
  businessTypes: Yup.string().required(registerErrorI18n('business_type.blank')),
  contactName: Yup.string()
    .required(registerErrorI18n('contact_name.blank'))
    .matches(/^[\d\w\s\u0E00-\u0E7F]+$/, registerErrorI18n('contact_name.invalid')),
  contactPhoneNumber: Yup.string().required(registerErrorI18n('contact_phone_number.blank')),
  contactEmail: Yup.string()
    .required(registerErrorI18n('contact_email.blank'))
    .email(registerErrorI18n('contact_email.invalid')),
  contactLineId: Yup.string().required(registerErrorI18n('contact_line_id.blank')),
  referrerType: Yup.string(),
  isAcceptTermAndCondition: Yup.boolean()
    .default(false)
    .isTrue(registerErrorI18n('is_accept_term_and_condition.false')),
});

type RegisterUserValueType = Yup.InferType<typeof registerUserSchema>;

// eslint-disable-next-line import/prefer-default-export
export const RegisterPage = () => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: { referrerType: 'application' },
    resolver: yupResolver(registerUserSchema),
  });

  const { response: businessTypeResponse, isLoading: businessTypeLoading } = useHttpRequest({
    url: '/enums/business_types',
  });
  const businessTypeOptions: string[] | undefined = businessTypeResponse?.data;

  const { response: referrerTypeResponse, isLoading: referrerTypeLoading } = useHttpRequest({
    url: '/enums/findtemp_referrer_types',
  });
  const referrerTypeOptions: string[] | undefined = referrerTypeResponse?.data;

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const submitHandler = async (value: RegisterUserValueType) => {
    setErrorMessage(null);

    try {
      await apiAxios({
        method: 'POST',
        url: '/registration',
        data: {
          name: value.name,
          business_type: value.businessTypes,
          referrer_type: value.referrerType,
          contact_name: value.contactName,
          contact_email: value.contactEmail,
          contact_line_id: value.contactLineId,
          contact_phone_number: value.contactPhoneNumber,
          district: 'กรุงเทพฯ และปริมณฑล',
        },
      });

      // if status === OK
      navigate('/auth/thank_you');
    } catch (error) {
      const status = get(error, ['response', 'status']);
      const errors = get(error, ['response', 'data', 'errors']);

      if (status === 423) {
        navigate('/auth/reset_password');
      }

      if (isEmpty(errors)) {
        setErrorMessage(get(error, 'message', null));
      } else {
        setErrorMessage(joinPairs(errors));
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ paddingY: 2 }}>
      <WixNavigationBar />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          paddingY: 8,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="regular"
          textAlign="center"
          sx={{ display: 'inline-flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 1 } }}
        >
          <Typography variant="inherit">ลงทะเบียนใช้งาน</Typography>
          <Typography variant="inherit">และรับใบเสนอราคา</Typography>
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ marginTop: 1 }}>
          เริ่มต้นการบริหารจัดการพนักงานพาร์ทไทม์ของท่านอย่างมีประสิทธิภาพไปกับเรา
          <br />
          ด้วยพนักงาน TEMP ที่พร้อมทำงานในระบบจำนวนมากกว่า 200,000 คน ครอบคลุมทั่วประเทศไทย
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(submitHandler)}>
        <Grid container gap={2}>
          <Controller
            key="name"
            control={control}
            name="name"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('name')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="text" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="businessTypes"
            control={control}
            name="businessTypes"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('business_type')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <SelectWithOption
                      value={value ?? null}
                      fullWidth
                      {...field}
                      TextFieldFormControlProps={{ id: field.name, fullWidth: true, error: invalid }}
                    >
                      {map(businessTypeOptions, (businessType) => (
                        <MenuItem key={businessType} value={businessType}>
                          {labelFieldEnum('register.business_types', businessType)}
                        </MenuItem>
                      ))}
                    </SelectWithOption>
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="contactName"
            control={control}
            name="contactName"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('contact_name')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="text" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="contactPhoneNumber"
            control={control}
            name="contactPhoneNumber"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('contact_phone_number')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="tel" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="contactEmail"
            control={control}
            name="contactEmail"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('contact_email')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="email" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="contactLineId"
            control={control}
            name="contactLineId"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('contact_line_id')} *</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <TextField id={field.name} type="text" fullWidth value={value ?? ''} error={invalid} {...field} />
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="referrerType"
            control={control}
            name="referrerType"
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid item container alignItems="center" gap={1}>
                  <Grid item xs={3}>
                    <FormLabel>{registerFieldI18n('referrer_type', { app_name: APP_NAME })}</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <SelectWithOption
                      value={value ?? null}
                      fullWidth
                      {...field}
                      TextFieldFormControlProps={{ id: field.name, fullWidth: true, error: invalid }}
                    >
                      {map(referrerTypeOptions, (referrerType) => (
                        <MenuItem key={referrerType} value={referrerType}>
                          {labelFieldEnum('register.referrer_types', referrerType)}
                        </MenuItem>
                      ))}
                    </SelectWithOption>
                  </Grid>
                </Grid>
                <Grid item container gap={1}>
                  <Grid item xs={3} />
                  <Grid item xs>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </FormControl>
            )}
          />

          <Controller
            key="isAcceptTermAndCondition"
            control={control}
            name="isAcceptTermAndCondition"
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <FormControlLabel
                  value={value}
                  onChange={onChange}
                  control={<Checkbox />}
                  label={
                    <Typography color={invalid ? 'error' : undefined}>
                      {registerPageI18n('accept_condition_1', { app_name: APP_NAME })}
                      <LinkMui href="https://www.findtemp.in.th/term-condition" target="_blank" rel="noopener">
                        {i18n.t('general.term_condition')}
                      </LinkMui>
                      {` ${i18n.t('general.and')} `}
                      <LinkMui href="https://www.findtemp.in.th/privacy-policy" target="_blank" rel="noopener">
                        {i18n.t('general.privacy_policy')}
                      </LinkMui>
                      {registerPageI18n('accept_condition_2')} *
                    </Typography>
                  }
                />

                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            )}
          />

          <Collapse in={!!errorMessage}>
            <Alert severity="error">{errorMessage}</Alert>
          </Collapse>
          <LoadingButton type="submit" disabled={isSubmitting} loading={isSubmitting} fullWidth variant="contained">
            {i18n.t('general.register')}
          </LoadingButton>
        </Grid>
      </form>

      <BottomSafeArea />
    </Container>
  );
};
